import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Globe, Link2, ListTree, Mail, Network, Pencil, Plus, RefreshCw, RotateCcw, Server, Settings, Trash2, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { DNS_RECORD_TYPES, type DnsProviderInfo, type DnsRecord, type DnsRecordType, type DnsZoneDetail, type DnsZoneRecord } from "@hostdock/types";
import {
  Button, Checkbox, cn, ConfirmDialog, CopyButton, Dialog, ErrorState, Field, FormError, Input, Select, Skeleton, TechLogo, techFor, toast,
} from "@hostdock/ui";
import { api } from "../api";
import {
  actionsCell, Cell, EmptyCard, FilterTabs, InfoCard, InfoRow, PageHero, Pill, primaryButtonClass, rowPrimaryClass, rowSecondaryClass, SearchBox, secondaryButtonClass, StatCard,
  tableCell, TableShell, Toolbar,
} from "../components/page-kit";
import { DOCS_HREF } from "../layout";

const externalDocs = /^https?:\/\//.test(DOCS_HREF);
const ZONE_LABEL: Record<DnsZoneRecord["status"], string> = { ACTIVE: "Live", PENDING_NAMESERVERS: "Waiting for nameservers", ERROR: "Problem" };
const ZONE_TONE = { ACTIVE: "ok", PENDING_NAMESERVERS: "wait", ERROR: "bad" } as const;

function ZonePill({ status }: { status: DnsZoneRecord["status"] }) {
  return <Pill tone={ZONE_TONE[status]} dot>{ZONE_LABEL[status]}</Pill>;
}

function ProviderName({ id, label }: { id: string; label?: string }) {
  const tech = techFor(id);
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      {tech && <TechLogo tech={tech} variant="plain" size="sm" />}
      <span className="truncate">{label ?? id.charAt(0).toUpperCase() + id.slice(1)}</span>
    </span>
  );
}

// ---------------------------------------------------------------- list

/**
 * Zone Editor, like cPanel's: one row per domain with quick "+ A / + CNAME / + MX record" and Manage. Every domain on
 * the customer's list has its zone here (HostDock DNS); a domain whose DNS runs at Cloudflare shows that connection.
 */
export function DnsPage() {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [quick, setQuick] = useState<{ zone: DnsZoneRecord; type: DnsRecordType } | null>(null);
  const [search, setSearch] = useState("");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["dns-zones"], queryFn: () => api.dns.zones(), refetchInterval: 30_000 });
  const providers = useQuery({ queryKey: ["dns-providers"], queryFn: () => api.dns.providers(), staleTime: 300_000 });
  const providerLabel = useMemo(() => new Map(providers.data?.items.map((p) => [p.id, p.label])), [providers.data]);
  const items = q.data?.items;
  const offline = items?.some((z) => z.hosted && !z.dnsOnline);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items?.filter((z) => !term || z.domain.includes(term)).sort((a, b) => a.domain.localeCompare(b.domain));
  }, [items, search]);

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={Network}
        title="Zone Editor"
        description="The DNS records of every domain you add: created for you (website, www, mail, MX, SPF, DMARC and DKIM), and yours to add, edit and remove."
        action={<button type="button" onClick={() => setAdding(true)} className={secondaryButtonClass}><Settings size={15} />Use another DNS provider</button>}
      />

      {offline && (
        <div className="mt-[10px] flex gap-3 rounded-xl border border-[#cfe3fb] bg-[#eff5fc] p-4 text-[12.5px] dark:border-info/30 dark:bg-info-soft">
          <Server size={20} className="mt-0.5 shrink-0 text-[#2f6fd6]" />
          <div>
            <p className="text-[13px] font-semibold text-fg">Not published yet</p>
            <p className="mt-1 text-muted">HostDock's nameservers are not online yet, so keep your domains' current nameservers for now. Everything you set up here is saved and goes live as soon as they are; each zone then shows the nameservers to use.</p>
          </div>
        </div>
      )}

      {items && items.length > 0 && (
        <Toolbar>
          <span className="text-[12.5px] text-muted">{items.length} domain{items.length === 1 ? "" : "s"}</span>
          <SearchBox value={search} onChange={setSearch} placeholder="Search domains..." />
        </Toolbar>
      )}

      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !items || items.length === 0 ? (
          <EmptyCard
            icon={Network}
            title="No domains yet"
            body="Add a domain or subdomain on the Domains page; its DNS zone appears here right away with its records."
            action={<Link to="/domains" className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />Add a domain</Link>}
          />
        ) : rows && rows.length === 0 ? (
          <EmptyCard title="No matching domains" body="Try a different search." />
        ) : (
          <ZoneTable rows={rows ?? []} providerLabel={providerLabel} onOpen={(z) => navigate(`/dns/${z.id}`)} onQuick={(zone, type) => setQuick({ zone, type })} />
        )}
      </div>

      <InfoRow>
        <InfoCard
          to="/domains"
          icon={Link2}
          tile="bg-[#d5f4f6] text-[#0a8ea0]"
          className="bg-[#eef9fb] dark:bg-surface-2"
          title="Domains and subdomains"
          body="Add or remove domains and subdomains on the Domains page. A subdomain's records live in its parent domain's zone."
        />
        <InfoCard to={DOCS_HREF} external={externalDocs} icon={FileText} tile="bg-[#d9eafe] text-[#2f6fd6]" className="bg-[#eff5fc] dark:bg-surface-2" title="Need help with DNS?" body="What each record type does, how nameservers work, and how long DNS changes take to apply." />
      </InfoRow>

      <ConnectZoneDialog open={adding} onOpenChange={setAdding} onConnected={(z) => navigate(`/dns/${z.id}`)} />
      {quick && (
        <RecordDialog zone={quick.zone} record={null} initialType={quick.type} onClose={() => setQuick(null)}
          onDone={() => { void qc.invalidateQueries({ queryKey: ["dns-zones"] }); void qc.invalidateQueries({ queryKey: ["dns-records", quick.zone.id] }); }} />
      )}
    </div>
  );
}

function ZoneTable({ rows, providerLabel, onOpen, onQuick }: { rows: DnsZoneRecord[]; providerLabel: Map<string, string>; onOpen(z: DnsZoneRecord): void; onQuick(z: DnsZoneRecord, t: DnsRecordType): void }) {
  const cols = "xl:grid xl:grid-cols-[minmax(0,1fr)_88px_minmax(0,430px)_118px] xl:items-center";
  return (
    <TableShell label="Zones" cols={cols} headers={["Domain", "Records", "Add a record", "Actions"]}>
      {rows.map((z) => (
        <li key={z.id} role="row" aria-label={z.domain} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[71px] xl:gap-0 xl:py-[11px]")}>
          <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
            <span className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl bg-[#e3f5f8] text-[#0a8ea0] dark:bg-surface-3 dark:text-accent"><Network size={21} strokeWidth={1.7} /></span>
            <div className="min-w-0 leading-tight">
              <Link to={`/dns/${z.id}`} title={z.domain} className="block truncate text-[14px] font-bold hover:text-accent">{z.domain}</Link>
              <span className="mt-[5px] flex flex-wrap items-center gap-2 text-[11.5px] text-muted">
                {z.hosted ? (z.dnsOnline ? <ZonePill status={z.status} /> : <Pill tone="off" dot>Not published yet</Pill>) : <><ZonePill status={z.status} /><ProviderName id={z.provider} label={providerLabel.get(z.provider)} /></>}
              </span>
            </div>
          </div>
          <Cell label="Records" className={tableCell}><span className="tabular-nums">{z.recordCount ?? "—"}</span></Cell>
          <div role="cell" className="flex flex-wrap gap-2 xl:border-l xl:border-line xl:pl-[14px]">
            {QUICK_ADD.slice(0, 3).map((t) => (
              <button key={t} type="button" onClick={() => onQuick(z, t)} aria-label={`Add ${t} record to ${z.domain}`} className={cn(rowSecondaryClass, "flex-none px-[10px]")}><Plus size={14} className="shrink-0" />{t} Record</button>
            ))}
          </div>
          <div role="cell" className={actionsCell}>
            <button type="button" onClick={() => onOpen(z)} className={cn(rowPrimaryClass, "xl:w-[100px]")}><Settings size={15} className="shrink-0" />Manage</button>
          </div>
        </li>
      ))}
    </TableShell>
  );
}

function ConnectZoneDialog({ open, onOpenChange, onConnected }: { open: boolean; onOpenChange(o: boolean): void; onConnected(z: DnsZoneDetail): void }) {
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [credential, setCredential] = useState("");
  const providers = useQuery({ queryKey: ["dns-providers"], queryFn: () => api.dns.providers(), enabled: open });
  const zones = useQuery({ queryKey: ["dns-zones"], queryFn: () => api.dns.zones(), enabled: open });
  const doms = useQuery({ queryKey: ["domains"], queryFn: () => api.domains.list({ pageSize: 100 }), enabled: open });
  // Every domain already has its HostDock zone; this dialog is for running a domain's DNS at another provider.
  const provider = picked ?? providers.data?.items.find((p) => p.id !== "hostdock" && p.available)?.id ?? "cloudflare";
  const chosen: DnsProviderInfo | undefined = providers.data?.items.find((p) => p.id === provider);
  const hosted = provider === "hostdock";
  // A zone is made for a whole domain on the customer's list that has none yet (subdomains live in their parent's zone).
  const zoned = new Set(zones.data?.items.map((z) => z.domain));
  const candidates = (doms.data?.items ?? []).map((d) => d.name).filter((n, i, all) => all.indexOf(n) === i && !zoned.has(n) && ![...zoned].some((z) => n.endsWith(`.${z}`)) && !all.some((o) => n.endsWith(`.${o}`))).sort();
  const connect = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.dns.connect(hosted ? { domain, provider: "hostdock" } : { domain, provider: "cloudflare", credential }),
    onSuccess: (z) => { void qc.invalidateQueries({ queryKey: ["dns-zones"] }); toast.success(hosted ? `Zone for ${z.domain} created` : "Domain connected"); onOpenChange(false); setDomain(""); setCredential(""); onConnected(z); },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={hosted ? "Add a DNS zone" : "Use another DNS provider"}
      description={hosted ? undefined : "Run a domain's DNS at your own Cloudflare account instead of HostDock. Its HostDock zone is replaced; disconnecting brings it back."}
      footer={<><Button onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!domain || (!hosted && !credential)} loading={connect.isPending} onClick={() => connect.mutate()}>{hosted ? "Create zone" : "Connect"}</Button></>}>
      <div className="space-y-4">
        <Field label="DNS provider">{(p) => <Select {...p} value={provider} onChange={(e) => { setPicked(e.target.value); setDomain(""); }}>{(providers.data?.items ?? []).map((pr) => <option key={pr.id} value={pr.id} disabled={!pr.available}>{pr.label}{pr.available ? "" : " (unavailable)"}</option>)}</Select>}</Field>
        {hosted ? (
          candidates.length ? (
            <Field label="Domain" hint="One of your domains without a zone. It gets the usual records for your website and email.">{(p) => <Select {...p} value={domain} onChange={(e) => setDomain(e.target.value)}><option value="">Choose a domain…</option>{candidates.map((n) => <option key={n} value={n}>{n}</option>)}</Select>}</Field>
          ) : (
            <p className="rounded-lg border border-line bg-surface-2 p-3 text-[12.5px] text-muted">Every domain on your list already has its zone. <Link to="/domains" className="font-medium text-accent hover:underline">Add a domain</Link> to get a new one.</p>
          )
        ) : (
          <Field label="Domain">{(p) => <Input {...p} value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase())} placeholder="example.com" autoComplete="off" />}</Field>
        )}
        {chosen?.notes.map((n) => <p key={n} className="text-[12px] text-muted">{n}</p>)}
        {chosen && chosen.requiresCredential && (
          <Field label={chosen.credentialLabel} hint={chosen.credentialHelp}>{(p) => <Input {...p} type="password" value={credential} onChange={(e) => setCredential(e.target.value)} autoComplete="off" />}</Field>
        )}
        <FormError error={connect.error} />
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- detail

const WEB_TYPES: DnsRecordType[] = ["A", "AAAA", "CNAME"];
type RecordFilter = "all" | DnsRecordType;
type RecordGroup = "web" | "mail" | "txt" | "other";
const recordGroup = (t: DnsRecordType): RecordGroup => (WEB_TYPES.includes(t) ? "web" : t === "MX" ? "mail" : t === "TXT" ? "txt" : "other");
const TYPE_PILL: Record<RecordGroup, string> = {
  web: "bg-[#dff3f6] text-[#0a7f90] dark:bg-accent-soft dark:text-accent",
  mail: "bg-[#e6f0fc] text-[#2f6fd6] dark:bg-info-soft dark:text-info",
  txt: "bg-[#fdf0e3] text-[#c85a16] dark:bg-warning-soft dark:text-warning",
  other: "bg-surface-3 text-muted",
};

export function DnsZonePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState<DnsRecord | DnsRecordType | null>(null);
  const q = useQuery({ queryKey: ["dns-zone", id], queryFn: () => api.dns.zone(id), refetchInterval: 30_000 });
  const providers = useQuery({ queryKey: ["dns-providers"], queryFn: () => api.dns.providers(), staleTime: 300_000 });
  const refresh = useMutation({ meta: { inlineError: true }, mutationFn: () => api.dns.refresh(id), onSuccess: (z) => { qc.setQueryData(["dns-zone", id], z); toast.success(z.status === "ACTIVE" ? "Your domain is live" : "Still waiting for the nameserver change"); } });
  const disconnect = useMutation({ meta: { inlineError: true }, mutationFn: (domain: string) => api.dns.disconnect(id, domain), onSuccess: () => { toast.success("Domain disconnected"); void qc.invalidateQueries({ queryKey: ["dns-zones"] }); navigate("/dns"); } });
  const reset = useMutation({ meta: { inlineError: true },
    mutationFn: (domain: string) => api.dns.reset(id, domain),
    onSuccess: (z) => { qc.setQueryData(["dns-zone", id], z); void qc.invalidateQueries({ queryKey: ["dns-records", id] }); void qc.invalidateQueries({ queryKey: ["dns-zones"] }); setDeleting(false); toast.success("Zone reset to its default records"); },
  });
  if (q.isPending) return <div className="lg:px-[13px]"><Skeleton className="h-40 w-full rounded-xl" /></div>;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const z = q.data;
  const label = providers.data?.items.find((p) => p.id === z.provider)?.label;
  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={Network}
        title={z.domain}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/dns" className="inline-flex items-center gap-1 font-medium text-accent hover:underline"><ArrowLeft size={13} />Zone Editor</Link>
            <span className="inline-flex items-center gap-1.5">DNS at <ProviderName id={z.provider} label={label} /></span>
            {z.hosted && !z.dnsOnline ? <Pill tone="off" dot>Not published yet</Pill> : <ZonePill status={z.status} />}
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setDeleting(true)} className={cn(secondaryButtonClass, "text-danger")}>{z.hosted ? <><RotateCcw size={15} />Reset zone</> : <><Trash2 size={15} />Disconnect</>}</button>
            <button type="button" onClick={() => setEditing("A")} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />Add record</button>
          </div>
        }
      />

      {z.hosted && !z.dnsOnline && (
        <div className="mt-[10px] flex gap-3 rounded-xl border border-[#cfe3fb] bg-[#eff5fc] p-4 text-[12.5px] dark:border-info/30 dark:bg-info-soft">
          <Server size={20} className="mt-0.5 shrink-0 text-[#2f6fd6]" />
          <div>
            <p className="text-[13px] font-semibold text-fg">Not published yet</p>
            <p className="mt-1 text-muted">{z.nameserverInstructions}</p>
          </div>
        </div>
      )}

      {z.status !== "ACTIVE" && !(z.hosted && !z.dnsOnline) && (
        <div className={cn("mt-[10px] rounded-xl border p-4 text-[12.5px]", z.status === "ERROR" ? "border-danger/30 bg-danger-soft" : "border-[#f9dcc0] bg-[#fff8f0] dark:border-warning/30 dark:bg-warning-soft")}>
          <div className="flex flex-wrap items-start gap-3">
            <TriangleAlert size={22} className={cn("shrink-0", z.status === "ERROR" ? "text-danger" : "text-[#e0611f]")} />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-fg">{z.status === "ERROR" ? "HostDock can't reach this domain's DNS" : "Point your domain at these nameservers"}</p>
              {z.status === "ERROR" && z.statusReason && <p className="mt-1 text-danger">{z.statusReason}</p>}
              {z.hosted && z.status !== "ERROR" && z.statusReason && <p className="mt-1 text-muted">Right now: {z.statusReason}.</p>}
              {z.hosted && <p className="mt-1 text-muted">Until then, the records below are ready but the internet still asks your current DNS provider.</p>}
              {z.nameserverInstructions && <p className="mt-1 text-muted">{z.nameserverInstructions}</p>}
              {z.nameservers.length > 0 && (
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                  {z.nameservers.map((ns) => (
                    <li key={ns} className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-line bg-surface px-3 py-2">
                      <span className="flex min-w-0 items-center gap-2"><Server size={14} className="shrink-0 text-subtle" /><span className="truncate font-mono text-[12.5px]">{ns}</span></span>
                      <CopyButton value={ns} />
                    </li>
                  ))}
                </ul>
              )}
              <FormError error={refresh.error} />
            </div>
            <button type="button" onClick={() => refresh.mutate()} disabled={refresh.isPending} className={secondaryButtonClass}><RefreshCw size={15} className={refresh.isPending ? "animate-spin" : undefined} />Check again</button>
          </div>
        </div>
      )}

      <Records zone={z} editing={editing} setEditing={setEditing} />

      {z.hosted ? (
        <ConfirmDialog open={deleting} onOpenChange={setDeleting} title={`Reset the zone of ${z.domain}?`} confirmText={z.domain} confirmLabel="Reset zone" tone="danger"
          description="Every record you added or changed is removed, and the default records for this domain, its subdomains and its email are created again."
          loading={reset.isPending} error={reset.error} onConfirm={() => reset.mutate(z.domain)} />
      ) : (
        <ConfirmDialog open={deleting} onOpenChange={setDeleting} title={`Disconnect ${z.domain}?`} confirmText={z.domain} confirmLabel="Disconnect"
          description="HostDock stops managing this domain's DNS through the provider and forgets the API token (the zone stays untouched there). The domain's HostDock DNS zone takes over again in the Zone Editor."
          loading={disconnect.isPending} error={disconnect.error} onConfirm={() => disconnect.mutate(z.domain)} />
      )}
    </div>
  );
}

const NEEDS_PRIORITY = (t: DnsRecordType) => t === "MX" || t === "SRV";
/** Cloudflare's 1 means "automatic"; otherwise the friendliest whole unit. */
const ttlLabel = (s: number) => (s === 1 ? "Auto" : s % 86400 === 0 ? `${s / 86400} day${s === 86400 ? "" : "s"}` : s % 3600 === 0 ? `${s / 3600} hour${s === 3600 ? "" : "s"}` : s % 60 === 0 ? `${s / 60} min` : `${s}s`);
const CAN_PROXY = (t: DnsRecordType) => t === "A" || t === "AAAA" || t === "CNAME";

const QUICK_ADD: DnsRecordType[] = ["A", "CNAME", "MX", "TXT"];

function Records({ zone, editing, setEditing }: { zone: DnsZoneDetail; editing: DnsRecord | DnsRecordType | null; setEditing(r: DnsRecord | DnsRecordType | null): void }) {
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState<DnsRecord | null>(null);
  const [filter, setFilter] = useState<RecordFilter>("all");
  const [search, setSearch] = useState("");
  const q = useQuery({ queryKey: ["dns-records", zone.id], queryFn: () => api.dns.records(zone.id) });
  const refresh = () => { void q.refetch(); void qc.invalidateQueries({ queryKey: ["dns-zone", zone.id] }); };
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: (r: DnsRecord) => api.dns.deleteRecord(zone.id, r.id), onSuccess: () => { toast.success("Record deleted"); setDeleting(null); refresh(); } });
  const items = q.data?.items;
  const counts = useMemo(() => {
    const c = { all: 0, web: 0, mail: 0, txt: 0, other: 0 };
    for (const r of items ?? []) { c.all++; c[recordGroup(r.type)]++; }
    return c;
  }, [items]);
  // cPanel-style filter by record type (only the types this zone has).
  const typeTabs = useMemo(() => {
    const n = new Map<DnsRecordType, number>();
    for (const r of items ?? []) n.set(r.type, (n.get(r.type) ?? 0) + 1);
    return [["all", `All (${items?.length ?? 0})`] as const, ...DNS_RECORD_TYPES.filter((t) => n.has(t)).map((t) => [t, `${t} (${n.get(t)})`] as const)];
  }, [items]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items?.filter((r) => (filter === "all" || r.type === filter) && (!term || r.name.toLowerCase().includes(term) || r.content.toLowerCase().includes(term)));
  }, [items, filter, search]);
  const cols = "xl:grid xl:grid-cols-[92px_240px_minmax(0,1fr)_86px_112px_150px] xl:items-center";
  const quickAdd = (
    <div className="flex flex-wrap gap-2">
      {QUICK_ADD.map((t) => <button key={t} type="button" onClick={() => setEditing(t)} className={secondaryButtonClass}><Plus size={15} />{t} record</button>)}
    </div>
  );

  return (
    <>
      {items && (
        <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
          <StatCard icon={ListTree} tile="teal" label="Records" value={counts.all} />
          <StatCard icon={Globe} tile="green" label="Website records" value={counts.web} sub="A, AAAA and CNAME" />
          <StatCard icon={Mail} tile="blue" label="Mail records" value={counts.mail} sub="MX" />
          <StatCard icon={FileText} tile="orange" label="Text and other" value={counts.txt + counts.other} sub="TXT, SRV, CAA, NS…" />
        </div>
      )}
      {zone.hosted && items && <div className="mt-[15px]">{quickAdd}</div>}
      {items && items.length > 0 && (
        <Toolbar>
          <FilterTabs<RecordFilter>
            label="Filter records"
            value={filter}
            onChange={setFilter}
            items={typeTabs}
          />
          <SearchBox value={search} onChange={setSearch} placeholder="Search records..." />
        </Toolbar>
      )}
      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !items || items.length === 0 ? (
          <EmptyCard icon={ListTree} title="No records" body="Add an A, CNAME, MX, TXT or other record." action={<button type="button" onClick={() => setEditing("A")} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />Add record</button>} />
        ) : rows && rows.length === 0 ? (
          <EmptyCard title="No matching records" body="Try a different search or filter." />
        ) : (
          <TableShell label="DNS records" cols={cols} headers={["Type", "Name", "Value", "TTL", zone.hosted ? "Added by" : "Proxy", "Actions"]}>
            {rows?.map((r) => (
              <li key={r.id} role="row" aria-label={`${r.type} ${r.name}`} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[62px] xl:gap-0 xl:py-[7px]")}>
                <div role="cell" className="flex items-center gap-3 xl:pr-3">
                  <span className={cn("inline-flex h-[27px] min-w-[58px] items-center justify-center rounded-lg px-2 text-[12px] font-semibold", TYPE_PILL[recordGroup(r.type)])}>{r.type}</span>
                  <span className="truncate font-semibold xl:hidden">{r.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
                  <Cell label="Name" className={cn(tableCell, "max-xl:hidden")}><span className="max-w-full truncate font-semibold" title={r.name}>{r.name}</span></Cell>
                  <Cell label="Value" className={cn(tableCell, "col-span-2 sm:col-span-4 xl:col-span-1")}>
                    <span className="max-w-full font-mono text-[12px] break-all xl:truncate xl:break-normal" title={r.content}>{r.priority !== null && <span className="text-muted">{r.priority} </span>}{r.content}</span>
                  </Cell>
                  <Cell label="TTL" className={tableCell}><span className="whitespace-nowrap text-muted" title={`${r.ttl} seconds`}>{ttlLabel(r.ttl)}</span></Cell>
                  {zone.hosted
                    ? <Cell label="Added by" className={tableCell}>{r.auto || r.locked ? <span title="Created by HostDock and kept up to date (for example when your website moves). Editing it makes it yours."><Pill tone="ok">HostDock</Pill></span> : <Pill tone="off">You</Pill>}</Cell>
                    : <Cell label="Proxy" className={tableCell}>{r.proxied === null ? <span className="text-subtle">—</span> : r.proxied ? <Pill tone="ok">Proxied</Pill> : <Pill tone="off">DNS only</Pill>}</Cell>}
                </div>
                <div role="cell" className={actionsCell}>
                  {r.locked ? <span className="text-[12px] text-subtle" title={zone.hosted ? "The domain's nameservers. They follow HostDock's nameserver settings." : undefined}>{zone.hosted ? "Set by HostDock" : "Managed by provider"}</span> : (
                    <>
                      <button type="button" onClick={() => setEditing(r)} className={cn(rowSecondaryClass, "xl:w-[78px]")}><Pencil size={14} className="shrink-0" />Edit</button>
                      <button type="button" onClick={() => setDeleting(r)} aria-label={`Delete ${r.type} ${r.name}`} title="Delete record" className={cn(rowSecondaryClass, "flex-none px-[10px] text-danger hover:bg-danger-soft xl:w-[40px]")}><Trash2 size={15} /></button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </TableShell>
        )}
      </div>
      {editing && <RecordDialog zone={zone} record={typeof editing === "string" ? null : editing} initialType={typeof editing === "string" ? editing : undefined} onClose={() => setEditing(null)} onDone={refresh} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Delete this record?" confirmLabel="Delete record" tone="danger"
        description={deleting ? `${deleting.type} ${deleting.name} → ${deleting.content}` : ""} loading={remove.isPending} error={remove.error} onConfirm={() => deleting && remove.mutate(deleting)} />
    </>
  );
}

/** A record's name as typed in a cPanel-style editor: "@" for the domain, else the part before it. */
const relativeName = (fqdn: string, zone: string) => (fqdn === zone ? "@" : fqdn.endsWith(`.${zone}`) ? fqdn.slice(0, -(zone.length + 1)) : fqdn);
const VALUE_HINT: Record<DnsRecordType, { label: string; placeholder: string; hint?: string }> = {
  A: { label: "IPv4 address", placeholder: "203.0.113.10" },
  AAAA: { label: "IPv6 address", placeholder: "2001:db8::1" },
  CNAME: { label: "Points to", placeholder: "example.net", hint: "A host name. Use @ for this domain." },
  MX: { label: "Mail server", placeholder: "mail.example.com" },
  TXT: { label: "Text", placeholder: "v=spf1 +a +mx ~all", hint: "SPF, DKIM, DMARC, site verification codes… without surrounding quotes." },
  SRV: { label: "Weight, port and target", placeholder: "5 5060 sip.example.com" },
  CAA: { label: "Flags, tag and value", placeholder: "0 issue \"letsencrypt.org\"" },
  NS: { label: "Nameserver", placeholder: "ns1.other-provider.net", hint: "Hands a subdomain over to other nameservers." },
};

function RecordDialog({ zone, record, initialType, onClose, onDone }: { zone: DnsZoneRecord; record: DnsRecord | null; initialType?: DnsRecordType; onClose(): void; onDone(): void }) {
  const [type, setType] = useState<DnsRecordType>(record?.type ?? initialType ?? "A");
  const [name, setName] = useState(record ? (zone.hosted ? relativeName(record.name, zone.domain) : record.name) : zone.hosted ? "@" : zone.domain);
  const [content, setContent] = useState(record?.content ?? "");
  const [ttl, setTtl] = useState(String(record?.ttl ?? (zone.hosted ? 14400 : 1)));
  const [priority, setPriority] = useState(String(record?.priority ?? 10));
  const [proxied, setProxied] = useState(record?.proxied ?? false);
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => {
      const input = { type, name, content, ttl: Number(ttl), ...(NEEDS_PRIORITY(type) ? { priority: Number(priority) } : {}), ...(CAN_PROXY(type) ? { proxied } : {}) };
      return record ? api.dns.updateRecord(zone.id, record.id, input) : api.dns.createRecord(zone.id, input);
    },
    onSuccess: () => { toast.success(record ? "Record updated" : "Record added"); onClose(); onDone(); },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={record ? "Edit record" : "Add record"}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name || !content} loading={save.isPending} onClick={() => save.mutate()}>{record ? "Save" : "Add record"}</Button></>}>
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
          <Field label="Type">{(p) => <Select {...p} value={type} onChange={(e) => setType(e.target.value as DnsRecordType)}>{DNS_RECORD_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}</Select>}</Field>
          {zone.hosted ? (
            <Field label="Name" hint={`${name === "@" || !name ? zone.domain : `${name}.${zone.domain}`} · @ is the domain itself`}>
              {(p) => (
                <div className="flex min-w-0 items-center gap-2">
                  <Input {...p} value={name} onChange={(e) => setName(e.target.value.toLowerCase().trim())} placeholder="@ or www" autoComplete="off" className="min-w-0 flex-1" />
                  <span className="max-w-[45%] shrink-0 truncate text-[12.5px] text-muted" title={zone.domain}>.{zone.domain}</span>
                </div>
              )}
            </Field>
          ) : (
            <Field label="Name" hint={`Use ${zone.domain} for the domain itself.`}>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value.toLowerCase())} autoComplete="off" />}</Field>
          )}
        </div>
        <Field label={VALUE_HINT[type].label} hint={VALUE_HINT[type].hint}>{(p) => <Input {...p} value={content} onChange={(e) => setContent(e.target.value)} placeholder={VALUE_HINT[type].placeholder} autoComplete="off" spellCheck={false} />}</Field>
        <div className="grid gap-3 sm:grid-cols-2">
          {NEEDS_PRIORITY(type) && <Field label="Priority" hint={type === "MX" ? "Lower is tried first." : undefined}>{(p) => <Input {...p} inputMode="numeric" value={priority} onChange={(e) => setPriority(e.target.value.replace(/\D/g, ""))} />}</Field>}
          <Field label="TTL" hint="How long resolvers may cache it.">{(p) => (
            <Select {...p} value={ttl} onChange={(e) => setTtl(e.target.value)}>
              {zone.hosted ? <option value="14400">4 hours (default)</option> : <option value="1">Automatic</option>}
              <option value="300">5 minutes</option><option value="3600">1 hour</option><option value="86400">1 day</option>
              {!["1", "14400", "300", "3600", "86400"].includes(ttl) && <option value={ttl}>{ttlLabel(Number(ttl))}</option>}
            </Select>
          )}</Field>
        </div>
        {zone.hosted && record?.auto && <p className="text-[12px] text-muted">HostDock created this record and keeps it up to date. Once you change it, it is yours and HostDock leaves it alone.</p>}
        {CAN_PROXY(type) && zone.provider === "cloudflare" && <Checkbox checked={proxied} onChange={() => setProxied((v) => !v)} label="Proxy through Cloudflare (orange cloud)" />}
        <FormError error={save.error} />
      </div>
    </Dialog>
  );
}
